import React, { useRef } from 'react';
import { COLORS, TYPE, WEIGHT, LETTER, SPACE } from '../styles/tokens';
import GlassCard from './ui/GlassCard';
import IconBadge from './ui/IconBadge';
import SectionHeading from './ui/SectionHeading';
import useScrollReveal from '../hooks/useScrollReveal';
import { useT } from '../i18n/I18nContext';
import { useTrackSection } from '../hooks/useTrackSection';

const ICONS: React.ReactNode[] = [
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z"
          stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>,
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18"
          stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>,
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="white" strokeWidth="1.8" />
    <path d="M12 8v4l3 3" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>,
];
const TINTS = [COLORS.tintPink, COLORS.tintPurple, COLORS.tintBlue];
const GLOWS = [COLORS.glowPink, COLORS.glowPurple, COLORS.glowBlue];

export default function ProblemSection() {
  const t = useT();
  const sectionRef = useRef<HTMLElement>(null);
  const headRef  = useScrollReveal<HTMLDivElement>({ fromY: 28, duration: 0.75, start: 'top 80%' });
  const cardsRef = useScrollReveal<HTMLDivElement>({
    fromY: 22, fromScale: 0.97, duration: 0.6, stagger: 0.1, start: 'top 85%',
    selector: '.pain-card',
  });
  useTrackSection('problem', sectionRef);

  return (
    <section
      ref={sectionRef}
      id="pain"
      style={{
        position: 'relative',
        padding: `${SPACE.sectionY} ${SPACE.sectionX} clamp(56px, 7vw, 96px)`,
        zIndex: 1,
        background: 'transparent',
      }}
    >
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div ref={headRef} style={{ marginBottom: 'clamp(32px, 4vw, 52px)', opacity: 0 }}>
          <SectionHeading title={t.problem.titleA} secondLine={t.problem.titleB} />
        </div>

        <div
          ref={cardsRef}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: SPACE.gridGap,
          }}
          className="pain-grid"
        >
          {t.problem.cards.map((p, i) => (
            <GlassCard key={i} hoverGlow={GLOWS[i]} className="pain-card">
              <div style={{ marginBottom: 16 }}>
                <IconBadge tint={TINTS[i]}>{ICONS[i]}</IconBadge>
              </div>
              <h3 style={{
                fontSize: TYPE.cardTitle, fontWeight: WEIGHT.semibold,
                color: COLORS.textPrimary,
                marginBottom: 8, lineHeight: 1.3,
                letterSpacing: LETTER.tight,
              }}>
                {p.title}
              </h3>
              <p style={{
                fontSize: TYPE.cardText, color: COLORS.textBody,
                lineHeight: 1.6, fontWeight: WEIGHT.regular,
              }}>
                {p.text}
              </p>
            </GlassCard>
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 640px) {
          .pain-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
